import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { LoadingState, StatusView, traceButtonClass, type TraceSession } from '@traceio-org/auth-ui';
import { PRODUCTS, type NavigationDenyReason, type ProductKey } from '@traceio-org/platform-contracts';
import { organizationNameOf, useOptionalMeAccess } from '../app/access';
import { forgetCameFrom } from '../app/came-from';
import { useBootstrap, useHub } from '../app/context';
import {
  denyReasonFromSearch,
  loginRoute,
  productFromSearch,
  productQuery,
  readHubState,
  usePageHeading,
} from '../app/hooks';
import { AccountActions, HubFrame } from '../app/ui';
import { settingsHeaderActions, settingsHeaderLink } from '../settings/entry';
import { administrableContexts } from '../settings/permissions';

function productName(productKey: ProductKey | null): string {
  return productKey ? PRODUCTS[productKey].displayName : 'this product';
}

function deniedCopy(
  reason: NavigationDenyReason | null,
  productKey: ProductKey | null,
  organization: string | undefined,
): { title: string; body: string } {
  const product = productName(productKey);
  const org = organization ?? 'your organization';
  switch (reason) {
    case 'product_not_entitled':
      return {
        title: `You do not have access to ${product}`,
        body: `Ask an administrator of ${org} for access to ${product}.`,
      };
    case 'membership_required':
      return {
        title: 'You are not a member of this workspace',
        body: `Ask an administrator of ${org} to invite you.`,
      };
    case 'membership_suspended':
      return {
        title: 'Your access is suspended',
        body: `Your membership of ${org} is suspended. Ask an administrator of ${org} to restore it.`,
      };
    case 'organization_suspended':
      return {
        title: 'This workspace is suspended',
        body: `The workspace of ${org} is suspended in Trace. Contact an administrator of your organization.`,
      };
    case 'product_unknown':
      return {
        title: 'This product does not exist',
        body: 'Check the address you used, or choose another product.',
      };
    case 'portal_scope_required':
      return {
        title: 'You need customer portal access',
        body: 'Ask your logistics provider to invite you to their customer portal.',
      };
    case 'staff_required':
      return {
        title: 'Trace Admin is for Trace staff only',
        body: 'This account is not a Trace staff account. Sign in with your staff account, or go to your own workspace.',
      };
    case 'host_unknown':
      return {
        title: 'This address is not set up for Trace',
        body: 'Sign in from the Trace sign-in page instead.',
      };
    case 'host_mismatch':
      return {
        title: 'This address belongs to a different workspace',
        body: "Sign in from your own organization's address instead.",
      };
    case 'context_not_found':
      return {
        title: 'That workspace is not available to you',
        body: 'It may have been removed, or your access may have changed.',
      };
    case null:
      return { title: 'You do not have access', body: `Ask an administrator of ${org} for access.` };
  }
}

/** /access-denied?reason=&product=: the reason in plain words and the ways forward. */
export function AccessDeniedPage() {
  const location = useLocation();
  const reason = denyReasonFromSearch(location.search);
  const productKey = productFromSearch(location.search);
  const { contextId } = useMemo(() => readHubState(location.state), [location.state]);
  const access = useOptionalMeAccess();
  const contexts = access.status === 'ready' && access.data ? access.data.contexts : [];
  const organization = organizationNameOf(contexts.find((c) => c.id === contextId));
  const copy = deniedCopy(reason, productKey, organization);
  const headingRef = usePageHeading(copy.title);
  // On host_mismatch or host_unknown this address itself is the problem: any other workspace chosen here
  // would be refused again (a loop), so none is offered; the copy points to the person's own address.
  const refusedByHost = reason === 'host_mismatch' || reason === 'host_unknown';
  const others = refusedByHost ? [] : contexts.filter((c) => c.id !== contextId);

  return (
    <HubFrame productKey={productKey}>
      <StatusView
        tone="warning"
        title={copy.title}
        headingRef={headingRef}
        primaryAction={
          others.length > 0 ? (
            <Link className={traceButtonClass('primary', { block: true })} to="/select-organization">
              Choose another workspace
            </Link>
          ) : null
        }
        footer={<AccountActions productKey={productKey} />}
      >
        <p>{copy.body}</p>
      </StatusView>
    </HubFrame>
  );
}

/**
 * /no-workspace: signed in, but no workspace has a product open to this person. Someone who administers an
 * organization (a billing administrator without products, for one) is pointed to its settings instead.
 */
export function NoWorkspacePage() {
  const access = useOptionalMeAccess();
  const email = access.status === 'ready' ? access.data?.subject.email : undefined;
  const contexts = access.status === 'ready' && access.data ? access.data.contexts : null;
  const managed = contexts ? administrableContexts(contexts) : [];
  const title =
    managed.length > 0 ? 'No Trace product is open to you yet' : 'You are not in a Trace workspace yet';
  const headingRef = usePageHeading(title);
  if (managed.length > 0) {
    const names = managed.map((context) => context.organization.displayName).join(', ');
    return (
      <HubFrame productKey={null} headerActions={settingsHeaderActions(contexts)}>
        <StatusView
          tone="info"
          title={title}
          headingRef={headingRef}
          primaryAction={settingsHeaderLink(contexts, traceButtonClass('primary', { block: true }))}
          footer={<AccountActions productKey={null} />}
        >
          <p>
            {email ? `You are signed in as ${email}, and you manage ${names}.` : `You manage ${names}.`} No
            Trace product is open to you there yet.
          </p>
          <p>Ask an administrator of your organization if you need a product.</p>
        </StatusView>
      </HubFrame>
    );
  }
  return (
    <HubFrame productKey={null}>
      <StatusView
        tone="info"
        title={title}
        headingRef={headingRef}
        footer={<AccountActions productKey={null} />}
      >
        <p>
          {email
            ? `You are signed in as ${email}, but this account is not a member of any Trace workspace.`
            : 'You are signed in, but this account is not a member of any Trace workspace.'}
        </p>
        <p>
          If someone invited you, open the link in your invitation email. An invitation only works for the
          email address it was sent to.
        </p>
      </StatusView>
    </HubFrame>
  );
}

/** /unavailable?product=: the product is not deployed here, or a redirect was refused. */
export function UnavailablePage() {
  const location = useLocation();
  const productKey = productFromSearch(location.search);
  const { unavailableReason } = useMemo(() => readHubState(location.state), [location.state]);
  const name = productKey ? PRODUCTS[productKey].displayName : 'This product';
  const title = `${name} is not available here yet`;
  const headingRef = usePageHeading(title);
  const body =
    unavailableReason === 'destination_refused'
      ? `Trace stopped a redirect to an address that is not registered for ${name}. Nothing was opened.`
      : unavailableReason === 'disabled_in_environment'
        ? `${name} is turned off in this environment.`
        : `${name} is not deployed in this environment yet.`;
  return (
    <HubFrame productKey={productKey}>
      <StatusView
        tone="info"
        title={title}
        headingRef={headingRef}
        primaryAction={
          <Link className={traceButtonClass('primary', { block: true })} to="/select-organization">
            Choose another product
          </Link>
        }
        secondaryAction={
          <Link className={traceButtonClass('quiet', { block: true })} to={loginRoute(productKey)}>
            Back to sign-in
          </Link>
        }
      >
        <p>{body}</p>
      </StatusView>
    </HubFrame>
  );
}

/** /session-expired?product=: shown once; signing in again is always the person's choice. */
export function SessionExpiredPage() {
  const { sessions } = useHub();
  const location = useLocation();
  const productKey = productFromSearch(location.search);
  const title = 'Your session has ended';
  const headingRef = usePageHeading(title);
  useEffect(() => {
    sessions.clear();
  }, [sessions]);
  return (
    <HubFrame productKey={productKey}>
      <StatusView
        tone="info"
        title={title}
        headingRef={headingRef}
        primaryAction={
          <Link className={traceButtonClass('primary', { block: true })} to={loginRoute(productKey)}>
            Sign in again
          </Link>
        }
      >
        <p>For your security, sign in again to continue.</p>
      </StatusView>
    </HubFrame>
  );
}

/** /signed-out?product=: confirmation with a way back to the sign-in page it came from. */
export function SignedOutPage() {
  const { sessions } = useHub();
  const location = useLocation();
  const productKey = productFromSearch(location.search);
  const title = 'You are signed out';
  const headingRef = usePageHeading(title);
  useEffect(() => {
    sessions.clear();
  }, [sessions]);
  return (
    <HubFrame productKey={productKey}>
      <StatusView
        tone="success"
        title={title}
        headingRef={headingRef}
        primaryAction={
          <Link className={traceButtonClass('primary', { block: true })} to={loginRoute(productKey)}>
            Back to sign-in
          </Link>
        }
      >
        <p>You are signed out of Trace. This does not sign you out of Microsoft or Google.</p>
      </StatusView>
    </HubFrame>
  );
}

/**
 * /logout?product=: ends the development session or the Auth0 session (Trace only, not Microsoft or
 * Google), clears the in-memory state and lands on /signed-out. With Auth0 configured the provider
 * logout always runs, so the provider session cannot silently sign the next person in. When ending a
 * session fails, the page says so and keeps the session to retry; it never claims a sign-out that did
 * not happen.
 */
export function LogoutPage() {
  const deps = useHub();
  const location = useLocation();
  const navigate = useNavigate();
  const productKey = productFromSearch(location.search);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const started = useRef(-1);
  // The session being ended, kept across retries; undefined until it has been looked up.
  const target = useRef<TraceSession | null | undefined>(undefined);
  const title = failed ? 'We could not sign you out' : 'Signing out';
  const headingRef = usePageHeading(title);

  useEffect(() => {
    if (started.current === attempt) return;
    started.current = attempt;
    const signedOutPath = `/signed-out${productQuery(productKey)}`;
    const returnTo = `${deps.location.origin}${signedOutPath}`;
    // The way back to a product belongs to the person who signed in: on a shared computer the next person must
    // not be offered it.
    forgetCameFrom();
    void (async () => {
      try {
        if (target.current === undefined) {
          let found: TraceSession | null = deps.sessions.active();
          // isAuthenticated answers false for "no session" and throws when it cannot tell.
          if (!found && deps.sessions.dev && (await deps.sessions.dev.isAuthenticated()))
            found = deps.sessions.dev;
          if (!found && deps.sessions.auth0) found = deps.sessions.auth0;
          target.current = found;
        }
        if (!target.current) {
          deps.sessions.clear();
          navigate(signedOutPath, { replace: true });
          return;
        }
        await target.current.signOut(returnTo);
        deps.sessions.clear();
      } catch (error) {
        console.warn('account-hub: sign-out failed', error);
        setFailed(true);
      }
    })();
  }, [attempt, deps, navigate, productKey]);

  return (
    <HubFrame productKey={productKey}>
      {failed ? (
        <StatusView
          tone="error"
          title={title}
          headingRef={headingRef}
          primaryAction={{
            label: 'Try again',
            onClick: () => {
              setFailed(false);
              setAttempt((n) => n + 1);
            },
          }}
        >
          <p>Check your connection, then try again.</p>
        </StatusView>
      ) : (
        <>
          <h1 ref={headingRef} tabIndex={-1} className="trace-auth-title">
            {title}
          </h1>
          <LoadingState label="Signing out" />
        </>
      )}
    </HubFrame>
  );
}

/** Any other path. */
export function NotFoundPage() {
  const title = 'Page not found';
  const headingRef = usePageHeading(title);
  return (
    <HubFrame productKey={null}>
      <StatusView
        tone="info"
        title={title}
        headingRef={headingRef}
        primaryAction={
          <Link className={traceButtonClass('primary', { block: true })} to="/login">
            Go to sign-in
          </Link>
        }
      >
        <p>This page does not exist in the Trace account hub.</p>
      </StatusView>
    </HubFrame>
  );
}

/**
 * Unknown, unverified or unresolvable host: no sign-in form of any kind, only a link to Trace's own
 * sign-in page.
 */
export function NotSetUpPage({ reason }: { reason: 'not_set_up' | 'unreachable' }) {
  const deps = useHub();
  const { registry } = useBootstrap();
  const title = 'This address is not set up for Trace';
  const headingRef = usePageHeading(title);
  const hubOrigin = registry?.hub.origin ?? deps.config.hubOrigin;
  const host = deps.location.hostname;
  return (
    <HubFrame productKey={null}>
      <StatusView
        tone="warning"
        title={title}
        headingRef={headingRef}
        primaryAction={
          hubOrigin ? (
            <a className={traceButtonClass('primary', { block: true })} href={`${hubOrigin}/login`}>
              Go to the Trace sign-in
            </a>
          ) : null
        }
        secondaryAction={
          reason === 'unreachable' ? { label: 'Try again', onClick: () => window.location.reload() } : null
        }
      >
        <p>
          {reason === 'unreachable'
            ? `We could not confirm that ${host} belongs to a Trace workspace. Try again in a moment, or go to the Trace sign-in.`
            : `${host} is not connected to a Trace workspace. Check the address you were given, or go to the Trace sign-in.`}
        </p>
      </StatusView>
    </HubFrame>
  );
}
